import React from 'react';
import { arrayOf, oneOf, bool, object, node, shape, string } from 'prop-types';
import cx from 'classnames';

import { Checkbox, Icon } from 'up-design-system-gex/build/components';

const propTypes = {
    /** Is set, if the table doesn't have header. */
    headDisabled: bool,
    /** Head sizes */
    headSize: oneOf(['space']),
    /** Labels for columns in the table. Value of the key in columns match with the key name in data */
    columns: arrayOf(
        shape({
            key: string,
            label: node,
            thClasses: string,
            tdClasses: string,
            noSort: bool,
        })
    ),
    /** Data to print out in the table. */
    data: arrayOf(object),
    /** Table rows selectable */
    selectable: bool,
    /** Table rows sortable */
    sortable: bool,
    /** Table notice */
    notice: node,
    /** Stripe background color reversed */
    bgReverse: bool,
};

const CLASS_ROOT = 'table';

const Table = ({
    className,
    columns,
    data,
    headDisabled = false,
    type,
    striped,
    selectable,
    sortable,
    notice,
    bgReverse,
    headSize,
    sortCallback, // added by Ondrca
    sorting, // added by Ondrca
    ...other
}) => {
    const classes = cx(
        CLASS_ROOT,
        {
            [`${CLASS_ROOT}--reversed`]: bgReverse,
        },
        className
    );

    function noLabel(element) {
        return element.label === '';
    }

    const tableHead =
        headDisabled || columns.every(noLabel) ? null : (
            <thead
                className={cx(`${CLASS_ROOT}__head`, {
                    [`${CLASS_ROOT}__head--${headSize}`]: headSize,
                })}
            >
                <tr>
                    {selectable && (
                        <th className={`${CLASS_ROOT}__checkbox`}>
                            <Checkbox
                                id="check-page"
                                size="small"
                                formControlClass="mb-none"
                            />
                        </th>
                    )}
                    {columns.map((column, i) => {
                        const columnSortable = sortable && !column.noSort;

                        const headClasses = cx(column.thClasses, {
                            clickable: columnSortable,
                        });

                        return (
                            <th key={i.toString()}
                                className={headClasses + ` ${sorting.key === column.key ? 'table__column--sorted' : ''}`}
                                onClick={
                                    () => {
                                        if (!columnSortable) return;
                                        sortCallback(column);
                                    }
                                }
                            >
                                {column.label}
                                {columnSortable && (
                                    <Icon
                                        name="triangle-down"
                                        className="text-vam mb-tiny icon--right"
                                        style={{
                                            "transform": `rotateZ(${(sorting.key === column.key ? sorting.dir : false) ? 180 : 0}deg)`
                                        }}
                                    />
                                )}
                            </th>
                        );
                    })}
                </tr>
            </thead>
        );

    const columnsLength = selectable ? columns.length + 1 : columns.length;

    const tableNotice = notice && (
        <tr className={`${CLASS_ROOT}__notice`}>
            <td colSpan={columnsLength}>{notice}</td>
        </tr>
    );

    const table = (
        <div className="table-container">
            <table className={classes} {...other}>
                {tableHead}
                <tbody>
                    {tableNotice}
                    {data.length ? (
                        data.map((row, i) => (
                            <tr key={i.toString()}>
                                {selectable && (
                                    <td>
                                        <Checkbox
                                            id={`check-row-${i}`}
                                            name={`check-row-${i}`}
                                            size="small"
                                            formControlClass="mb-none"
                                        />
                                    </td>
                                )}
                                {columns.map(column => (
                                    <td
                                        key={column.key}
                                        colSpan={column.colSpan}
                                        className={column.tdClasses}
                                    >
                                        {row[column.key]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr className={`${CLASS_ROOT}__row--empty`}>
                            <td colSpan={columnsLength}>Tabuľka neobsahuje žiadne údaje</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return table;
};

Table.displayName = 'Table';
Table.propTypes = propTypes;

export default Table;